import { Checkbox, Col, Divider, message, Modal, Pagination, Row, Table } from "antd";
import { TabsProps } from "antd/lib";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilValue } from "recoil";
import {
  buildColumnsWithHeaderFilters,
  HeaderTable,
  Tabs,
} from "../../Components";
import { Setting } from "../../assets/icon";
import type { ColumnWithFilter } from "../../Components/FilterBar/FilterBar";
import { PermissionDirect } from "../../Components/PermissionDirect";
import { OrderCostSheetDetail, useOrderCostSheet } from "../OrderCostSheet";
import { CommonApi, OrderApi, getPermissionDirections } from "../../services";
import { UserAtom } from "../../store";
import { checkPermissions } from "../../utils/checkPermissions";
import { updateColumnDisplayState } from "../../shared/helpers/tableColumns";
import { formatOrderStagePeriod } from "../../shared/helpers/orderStagePeriod";
import { resetPaginationToFirstPage } from "../../shared/helpers/pagination";
import { getOrderDirection } from "../Orders/helpers";
import styles from "./IncurredCosts.module.scss";

const incurredCostStatusByTab: Record<string, string | undefined> = {
  2: "WAITING_APPROVE_L1",
  3: "WAITING_APPROVE_L2",
};

const tabItems: TabsProps["items"] = [
  {
    key: "1",
    label: "Lịch sử đơn hàng",
  },
  {
    key: "2",
    label: "Duyệt cấp 1",
  },
  {
    key: "3",
    label: "Duyệt cấp 2",
  },
];

const apiDebounceRegion = (search: string) => {
  return CommonApi.getSearchRegion({ searchText: search }).then(
    (res: any) => res.data || [],
  );
};

const getColumnPositionKey = (dataIndex: any) => {
  return Array.isArray(dataIndex) ? dataIndex.join(".") : String(dataIndex);
};

const incurredCostTabKeys = ["1", "2", "3"];

const getSafeIncurredCostTab = (tab?: string | null) =>
  incurredCostTabKeys.includes(String(tab)) ? String(tab) : "1";

const IncurredCosts = () => {
  const history = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const tabs = query.get("tabs");
  const permissionState = useRecoilValue<any>(UserAtom.permissions);
  const features = permissionState?.features;
  const [messageApi, contextHolder] = message.useMessage();
  const [activeTab, setActiveTab] = useState(getSafeIncurredCostTab(tabs));
  const [filters, setFilters] = useState<any>({});
  const [permissionDirections, setPermissionDirections] = useState<
    { label: string; value: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [onModalPostion, setOnModalPosition] = useState(false);
  const options = {
    ORDER_V2_STATUS: [
      { label: "Chờ duyệt", value: "PENDING_APPROVAL", color: "#0082FB" },
      { label: "Đang vận chuyển", value: "SHIPPING", color: "#FF6200" },
      { label: "Đã kết thúc", value: "FINISHED", color: "#00B517" },
      { label: "Đã hủy", value: "CANCELLED", color: "#FF0000" },
    ],
    ORDER_V2_COST_STATUS: [
      { label: "Khởi tạo", value: "INIT", color: "#0082FB" },
      {
        label: "Chờ duyệt cấp 1",
        value: "WAITING_APPROVE_L1",
        color: "#FF6200",
      },
      {
        label: "Chờ duyệt cấp 2",
        value: "WAITING_APPROVE_L2",
        color: "#FF6200",
      },
      { label: "Đã duyệt", value: "APPROVED", color: "#00B517" },
      { label: "Đã đóng", value: "CLOSED", color: "#FF0000" },
    ],
    ORDER_TRANSPORT_TYPE: [
      { label: "Nhập", value: "IMPORT" },
      { label: "Xuất", value: "EXPORT" },
      { label: "Bắc - Nam", value: "ROAD" },
      { label: "Chuyển kho", value: "TRANSFER" },
    ],
    ORDER_CONT_TYPE: [20, 40, 45, 48],
    ORDER_DELIVERY_METHOD: [
      { label: "Nguyên seal", value: "FULL_SEAL" },
      { label: "Kiểm đếm", value: "TALLY" },
    ],
    ORDER_OWNERSHIP: [
      { label: "ĐẦU TƯ", value: "INVESTMENT" },
      { label: "THUÊ KHÔ", value: "DRY_RENTAL" },
      { label: "DỊCH VỤ", value: "SERVICE" },
    ],
  };

  const {
    dataDetail,
    loadingDetail,
    savingDetail,
    isSavingDetail,
    closeDetail,
    handleOnEditDetail,
    handleOnChangeDetail,
    handleSaveDetail,
    handleSubmitOrderCostLevel1,
    handleReviewOrderCost,
  } = useOrderCostSheet({
    messageApi,
    features,
    onOrderCostStatusChange: (orderId, costStatus) => {
      setDataSource((prev) =>
        prev.map((item) =>
          item._id === orderId ? { ...item, costStatus } : item,
        ),
      );
    },
  });

  const handleIncurredFiltersChange = useCallback((nextFilters: any) => {
    setPagination((prev) => resetPaginationToFirstPage(prev));
    setFilters((prev: any) =>
      typeof nextFilters === "function" ? nextFilters(prev) : nextFilters,
    );
  }, []);

  const handleIncurredTabChange = useCallback(
    (tab: string) => {
      closeDetail();
      setPagination((prev) => resetPaginationToFirstPage(prev));
      setActiveTab(getSafeIncurredCostTab(tab));
    },
    [closeDetail],
  );

  const columns: ColumnWithFilter<any>[] = [
    {
      title: "#",
      dataIndex: "_id",
      width: 50,
      render: (_row: any, _record: any, index: number) => index + 1,
    },
    {
      title: "Mã đơn hàng",
      dataIndex: "code",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập mã đơn hàng",
        },
      },
    },
    {
      title: "Hình thức sở hữu",
      dataIndex: "ownership",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn hình thức sở hữu",
          options: options.ORDER_OWNERSHIP,
        },
      },
      render: (row: any) =>
        options.ORDER_OWNERSHIP.find((item) => item.value === row)?.label ||
        row,
    },
    {
      title: "Khu vực",
      dataIndex: ["region", "name"] as any,
      filterDataIndex: "region",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "debounce",
        filterProps: {
          placeholder: "Chọn khu vực",
          fetchOptions: apiDebounceRegion,
        },
      },
    },
    {
      title: "Ngày đóng hàng",
      dataIndex: "loadingDate",
      filterConfig: {
        type: "dateRange",
        filterProps: {
          placeholder: ["Từ ngày đóng", "Đến ngày đóng"],
        },
      },
      render: (row: any) => (row ? dayjs(row).format("DD/MM/YYYY") : ""),
    },
    {
      title: "Ngày trả hàng",
      dataIndex: "returnDate",
      filterConfig: {
        type: "dateRange",
        filterProps: {
          placeholder: ["Từ ngày trả", "Đến ngày trả"],
        },
      },
      render: (row: any) => (row ? dayjs(row).format("DD/MM/YYYY") : ""),
    },
    {
      title: "Khách hàng",
      dataIndex: "customerName",
      filterDataIndex: "customer",
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập khách hàng",
        },
      },
      render: (row: any) => row?.label || row,
    },
    {
      title: "Hình thức VC",
      dataIndex: "transportType",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn hình thức VC",
          options: options.ORDER_TRANSPORT_TYPE,
        },
      },
      render: (row: any) =>
        options.ORDER_TRANSPORT_TYPE.find((item) => item.value === row)
          ?.label || row,
    },
    {
      title: "Hàng hoá",
      dataIndex: "productName",
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập hàng hoá",
        },
      },
    },
    {
      title: "Số cont",
      dataIndex: "containerNumber",
      fixed: false,
      hidden: false,
    },
    {
      title: "Loại cont",
      dataIndex: "containerType",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn loại cont",
          options: options.ORDER_CONT_TYPE.map((item) => ({
            label: item,
            value: item,
          })),
        },
      },
    },
    {
      title: "Khối lượng (Tấn)",
      dataIndex: "weight",
      fixed: false,
      hidden: false,
    },
    {
      title: "Phương thức giao nhận",
      dataIndex: "deliveryMethod",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          placeholder: "Chọn phương thức giao nhận",
          options: options.ORDER_DELIVERY_METHOD,
        },
      },
      render: (row: any) =>
        options.ORDER_DELIVERY_METHOD.find((item) => item.value === row)
          ?.label || row,
    },
    {
      title: "PAKD",
      dataIndex: "businessPlanCode",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập mã PAKD",
        },
      },
    },
    {
      title: "Tuyến",
      dataIndex: "routeCode",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập tuyến",
        },
      },
      render: (row: any, record: any) =>
        row || record.businessPlan?.routeBaseCode,
    },
    {
      title: "Chiều hàng",
      dataIndex: "direction",
      filterDataIndex: "direction",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "select",
        filterProps: {
          showSearch: true,
          optionFilterProp: "label",
          options: permissionDirections,
          placeholder: "Nhập chiều hàng",
        },
      },
      render: (row: any, record: any) => row || getOrderDirection(record),
    },
    {
      title: "Điểm nhận",
      dataIndex: "receiptAddress",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập điểm nhận",
        },
      },
    },
    {
      title: "Điểm trả",
      dataIndex: "deliveryAddress",
      fixed: false,
      hidden: false,
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập điểm trả",
        },
      },
    },
    {
      title: "Điểm đóng chi tiết",
      dataIndex: "loadingDetail",
      fixed: false,
      hidden: false,
    },
    {
      title: "Điểm trả chi tiết",
      dataIndex: "returnDetail",
      fixed: false,
      hidden: false,
    },
    {
      title: "Nhà xe",
      dataIndex: "truckVendorName",
      filterDataIndex: "truckVendor",
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập nhà xe",
        },
      },
      render: (row: any) => row?.label || row,
    },
    {
      title: "Biển số xe",
      dataIndex: "licensePlate",
      filterDataIndex: "licensePlate",
      filterConfig: {
        type: "string",
        filterProps: {
          placeholder: "Nhập biển số xe",
        },
      },
    },
    {
      title: "Doanh thu",
      dataIndex: "actualRevenue",
      render: (row: any) => (row ? row.toLocaleString("vi-VN") : 0),
    },
    {
      title: "KH doanh thu",
      dataIndex: "combinedRevenueRate",
      fixed: false,
      hidden: false,
    },
    {
      title: "Chi phí thực tế",
      dataIndex: "actualCost",
      render: (row: any) => (row ? row.toLocaleString("vi-VN") : 0),
    },
    {
      title: "KH chi phí thầu",
      dataIndex: "combinedCostRate",
      fixed: false,
      hidden: false,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      fixed: "right",
      hidden: false,
      render: (row: any) => {
        const statusObj = options.ORDER_V2_STATUS.find(
          (item) => item.value === row,
        );
        return (
          <div style={{ color: statusObj?.color, fontWeight: "bold" }}>
            {statusObj ? statusObj.label : row}
          </div>
        );
      },
    },
    {
      title: "Trạng thái chi phí",
      dataIndex: "costStatus",
      fixed: "right",
      hidden: false,
      render: (row: any) => {
        const statusObj = options.ORDER_V2_COST_STATUS.find(
          (item) => item.value === row,
        );
        return (
          <div style={{ color: statusObj?.color, fontWeight: "bold" }}>
            {statusObj ? statusObj.label : row}
          </div>
        );
      },
    },
    {
      title: "Kỳ ghi nhận",
      dataIndex: "orderStagePeriod",
      fixed: false,
      hidden: false,
      render: formatOrderStagePeriod,
    },
  ];

  const [activeColumns, setActiveColumn] =
    useState<ColumnWithFilter<any>[]>(columns);

  useEffect(() => {
    setActiveColumn((prev) =>
      prev.map((col): ColumnWithFilter<any> => {
        if (getColumnPositionKey(col.dataIndex) !== "direction") return col;

        const filterConfig = col.filterConfig;
        if (!filterConfig) return col;

        return {
          ...col,
          filterConfig: {
            ...filterConfig,
            type: filterConfig.type,
            filterProps: {
              ...filterConfig.filterProps,
              options: permissionDirections,
            },
          },
        };
      }),
    );
  }, [permissionDirections]);

  const columnsRender = buildColumnsWithHeaderFilters(
    activeColumns.filter((col: any) => !col.hidden),
    filters,
    handleIncurredFiltersChange,
  );

  const handleOnChangePosition = (
    dataIndex: string,
    value: boolean | "left" | "right",
    type: "hidden" | "fixed",
  ) => {
    setActiveColumn((prev) =>
      updateColumnDisplayState(
        prev,
        dataIndex,
        value,
        type,
        (col) => getColumnPositionKey(col.dataIndex),
      ),
    );
  };

  const renderModalPosition = () => {
    return (
      <Modal
        open={onModalPostion}
        width={"70%"}
        onCancel={() => {
          setOnModalPosition(false);
        }}
        title="Hiển thị cột"
        bodyProps={{
          className: styles.ModalBody,
        }}
        footer={false}
      >
        <Row>
          <Col span={16}>Tên cột</Col>
          <Col span={4}>Hiển thị</Col>
          <Col span={4}>Cố định(trái, phải)</Col>
        </Row>
        <Divider />
        {activeColumns?.map((col: any) => {
          const columnKey = getColumnPositionKey(col.dataIndex);

          return (
            <div key={columnKey}>
              <Row>
                <Col span={16}>{col.title}</Col>
                <Col span={4}>
                  <Checkbox
                    checked={!col.hidden}
                    onChange={(e) => {
                      handleOnChangePosition(
                        columnKey,
                        e.target.checked,
                        "hidden",
                      );
                    }}
                  />
                </Col>
                <Col span={4}>
                  <Checkbox
                    checked={col.fixed === "left"}
                    onChange={(e) => {
                      handleOnChangePosition(
                        columnKey,
                        e.target.checked && "left",
                        "fixed",
                      );
                    }}
                    style={{ marginRight: "20px" }}
                  />
                  <Checkbox
                    checked={col.fixed === "right"}
                    onChange={(e) => {
                      handleOnChangePosition(
                        columnKey,
                        e.target.checked && "right",
                        "fixed",
                      );
                    }}
                  />
                </Col>
              </Row>
              <Divider />
            </div>
          );
        })}
      </Modal>
    );
  };

  useEffect(() => {
    const nextTab = getSafeIncurredCostTab(tabs);

    if (activeTab !== nextTab) {
      handleIncurredTabChange(nextTab);
    }

    if (tabs !== nextTab) {
      history.replace(`/operation/incurred-costs?tabs=${nextTab}`);
    }
  }, [activeTab, handleIncurredTabChange, history, tabs]);

  useEffect(() => {
    let mounted = true;

    getPermissionDirections({}).then((directions: any) => {
      if (!mounted) return;

      setPermissionDirections(
        (directions ?? []).map((direction: any) => ({
          label: direction.name,
          value: direction.code,
        })),
      );
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const resDataOrders: any = await OrderApi.getAllOrder({
          current: pagination.current,
          pageSize: pagination.pageSize,
          status: "FINISHED",
          costStatus: incurredCostStatusByTab[activeTab],
          ...filters,
          loadingDate: undefined,
          returnDate: undefined,
          loadingDateFrom: filters.loadingDate
            ? dayjs(filters.loadingDate[0]).startOf("day").toISOString()
            : undefined,
          loadingDateTo: filters.loadingDate
            ? dayjs(filters.loadingDate[1]).endOf("day").toISOString()
            : undefined,
          returnDateFrom: filters.returnDate
            ? dayjs(filters.returnDate[0]).startOf("day").toISOString()
            : undefined,
          returnDateTo: filters.returnDate
            ? dayjs(filters.returnDate[1]).endOf("day").toISOString()
            : undefined,
        });

        if (!mounted) return;
        setDataSource(resDataOrders?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          total: resDataOrders?.totalItem || 0,
        }));
      } catch (error: any) {
        messageApi.error(error?.message || "Lấy danh sách chi phí thất bại");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [activeTab, filters, messageApi, pagination.current, pagination.pageSize]);

  return (
    <PermissionDirect screen={["INCURRED"]}>
      <div className={styles.IncurredCosts}>
        {contextHolder}
        <div className="container">
          <HeaderTable
            title="Chi phí phát sinh"
            buttons={[
              {
                children: "Cài đặt hiển thị",
                icon: <Setting />,
                onClick: () => {
                  setOnModalPosition(true);
                },
                type: "primary",
              },
            ]}
          />
          <Tabs
            items={tabItems}
            activeKey={activeTab}
            onChange={(tab) => {
              const nextTab = getSafeIncurredCostTab(tab);
              handleIncurredTabChange(nextTab);
              history.push(`/operation/incurred-costs?tabs=${nextTab}`);
            }}
          />
          <Table
            columns={columnsRender}
            size="small"
            dataSource={dataSource}
            scroll={{ x: "max-content" }}
            style={{ width: "100%" }}
            pagination={false}
            loading={loading}
            onRow={(record: any) => ({
              onDoubleClick: () => {
                if (checkPermissions("INCURRED", "view", features)) {
                  handleOnEditDetail(
                    record._id,
                    record?.detailState || record?.costStatus || record?.state,
                  );
                } else {
                  message.error("Bạn không có quyền xem chi phí phát sinh");
                }
              },
            })}
            rowClassName={(record: any) =>
              dataDetail?._id && record._id === dataDetail?._id
                ? styles.HightLightRow
                : ""
            }
          />
          <Pagination
            total={pagination.total}
            current={pagination.current}
            pageSize={pagination.pageSize}
            onChange={(page, pageSize) => {
              setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize: pageSize || prev.pageSize,
              }));
            }}
            showSizeChanger
            pageSizeOptions={[5, 10, 20, 50, 100]}
            align="end"
            className={styles.Pagination}
          />
        </div>
        <OrderCostSheetDetail
          dataDetail={dataDetail}
          loadingDetail={loadingDetail}
          savingDetail={savingDetail}
          features={features}
          footerButtonClassName={styles.ButtonFooterTableDetail}
          showWorkflowActions={true}
          onClose={() => {
            if (isSavingDetail()) return;
            closeDetail();
          }}
          onChangeDetail={handleOnChangeDetail}
          onSaveDetail={handleSaveDetail}
          onSubmitLevel1={handleSubmitOrderCostLevel1}
          onReview={handleReviewOrderCost}
        />
      </div>
      {renderModalPosition()}
    </PermissionDirect>
  );
};

export { IncurredCosts };
